import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import Address from "@mapmycustomers/shared/types/Address";
import { Activity, MapEntity } from "@mapmycustomers/shared/types/entity";
import Route, { RouteManualStop, RoutePreview } from "@mapmycustomers/shared/types/entity/Route";
import { RouteEdgeEntity } from "@mapmycustomers/shared/types/entity/RouteDetail";
import {
  EntitiesSupportingRoutes,
  EntityTypesSupportingRoutes,
} from "@mapmycustomers/shared/types/entity/types";
import { GeoPoint } from "@mapmycustomers/shared/types/shapes";
import User from "@mapmycustomers/shared/types/User";

import { mapPinIdGetter } from "@app/util/map/idGetters";

import EdgeLocationType from "../types/EdgeLocationType";
import { EditorRouteStop } from "../types/RouteStop";

import type { RouteEditorState } from ".";

export const updateDetails = createAction("route/routeDetails/routeEditor/updateDetails")<
  Partial<
    Pick<
      RouteEditorState,
      "allottedTime" | "name" | "owner" | "routeType" | "startAt" | "travelMode"
    >
  >
>();

export const updateStartAddress = createAsyncAction(
  "route/routeDetails/routeEditor/updateStartAddress/request",
  "route/routeDetails/routeEditor/updateStartAddress/success",
  "route/routeDetails/routeEditor/updateStartAddress/failure"
)<{ address?: Address; geoPoint?: GeoPoint }, { address?: Address; geoPoint?: GeoPoint }, void>();

export const updateEndAddress = createAsyncAction(
  "route/routeDetails/routeEditor/updateEndAddress/request",
  "route/routeDetails/routeEditor/updateEndAddress/success",
  "route/routeDetails/routeEditor/updateEndAddress/failure"
)<{ address?: Address; geoPoint?: GeoPoint }, { address?: Address; geoPoint?: GeoPoint }, void>();

export const updateEndEdgeType = createAction(
  "route/routeDetails/routeEditor/updateEndEdgeType"
)<EdgeLocationType>();

export const updateEndEntity = createAsyncAction(
  "route/routeDetails/routeEditor/updateEndEntity/request",
  "route/routeDetails/routeEditor/updateEndEntity/success",
  "route/routeDetails/routeEditor/updateEndEntity/failure"
)<
  { entityId: RouteEdgeEntity["id"]; entityType: RouteEdgeEntity["entity"] },
  { entity: RouteEdgeEntity },
  void
>();

export const updateStartEdgeType = createAction(
  "route/routeDetails/routeEditor/updateStartEdgeType"
)<EdgeLocationType>();

export const updateStartEntity = createAsyncAction(
  "route/routeDetails/routeEditor/updateStartEntity/request",
  "route/routeDetails/routeEditor/updateStartEntity/success",
  "route/routeDetails/routeEditor/updateStartEntity/failure"
)<
  { entityId: RouteEdgeEntity["id"]; entityType: RouteEdgeEntity["entity"] },
  { entity: RouteEdgeEntity },
  void
>();

export const removeScheduledActivity = createAction(
  "route/routeDetails/routeEditor/removeScheduledActivity"
)<Activity["id"]>();

export const fetchScheduledActivities = createAsyncAction(
  "route/routeDetails/routeEditor/fetchScheduledActivities/request",
  "route/routeDetails/routeEditor/fetchScheduledActivities/success",
  "route/routeDetails/routeEditor/fetchScheduledActivities/failure"
)<void, Activity[], void>();

export const addPinsToRoute = createAction("route/routeDetails/routeEditor/addPinsToRoute")<{
  pins: MapEntity[];
}>();

export const removePinsFromRoute = createAction(
  "route/routeDetails/routeEditor/removePinsFromRoute"
)<{ pinIds: Array<ReturnType<typeof mapPinIdGetter>> }>();

export const updateRouteStops = createAsyncAction(
  "route/routeDetails/routeEditor/updateRouteStops/request",
  "route/routeDetails/routeEditor/updateRouteStops/success",
  "route/routeDetails/routeEditor/updateRouteStops/failure"
)<
  {
    addEntityIds?: Array<EntitiesSupportingRoutes["id"]>;
    entityType: EntityTypesSupportingRoutes;
    removeEntityIds?: Array<EntitiesSupportingRoutes["id"]>;
  },
  {
    addEntities?: EntitiesSupportingRoutes[];
    entityType: EntityTypesSupportingRoutes;
    removeEntityIds?: Array<EntitiesSupportingRoutes["id"]>;
  },
  void
>();

// `positional` marks an insert whose slot the user actually chose — today, the Edit sidebar's
// "+" gap. It is what demotes an OPTIMIZED route to ORDERED (see
// switchToOrderedOnPositionalInsert), because the next build would otherwise re-optimize the
// stop straight back out of the chosen slot. Appends carry no such choice and must leave the
// route optimized: the map POI popover, the record-row actions and EntityAnnotation all pass
// `insertAt: stopCount` without this flag.
export const insertStop = createAsyncAction(
  "route/routeDetails/routeEditor/insertStop/request",
  "route/routeDetails/routeEditor/insertStop/success",
  "route/routeDetails/routeEditor/insertStop/failure"
)<
  {
    entityId: EntitiesSupportingRoutes["id"];
    entityType: EntityTypesSupportingRoutes;
    insertAt: number;
    positional?: boolean;
  },
  {
    entity: EntitiesSupportingRoutes;
    entityType: EntityTypesSupportingRoutes;
    insertAt: number;
    positional?: boolean;
  },
  void
>();

export const updateStopDetails = createAction("route/routeDetails/routeEditor/updateStopDetails")<{
  details: Partial<Pick<EditorRouteStop, "allottedTime" | "checkedOff" | "scheduledTime">>;
  entityId: EditorRouteStop["id"];
  entityType: EditorRouteStop["entity"];
}>();

// Overwrites the time on every stop except activity-linked ones, whose time is the
// appointment length, and makes the value the route default for new stops.
export const applyAllottedTimeToAllStops = createAction(
  "route/routeDetails/routeEditor/applyAllottedTimeToAllStops"
)<number>();

// `positional` as on insertStop above.
export const addManualStop = createAction("route/routeDetails/routeEditor/addManualStop")<{
  insertAt: number;
  manualAddress: RouteManualStop["manualAddress"];
  manualGeoPoint: RouteManualStop["manualGeoPoint"];
  manualName: RouteManualStop["manualName"];
  positional?: boolean;
}>();

export const updateManualStop = createAction("route/routeDetails/routeEditor/updateManualStop")<{
  id: RouteManualStop["id"];
  manualAddress: RouteManualStop["manualAddress"];
  manualGeoPoint: RouteManualStop["manualGeoPoint"];
  manualName: RouteManualStop["manualName"];
}>();

export const removeManualStop = createAction("route/routeDetails/routeEditor/removeManualStop")<{
  id: RouteManualStop["id"];
}>();

export const reorderStop = createAction("route/routeDetails/routeEditor/reorderStop")<{
  fromIndex: number;
  toIndex: number;
}>();

export const invertStops = createAction("route/routeDetails/routeEditor/invertStops")<void>();

export const setSharedUsers = createAction("route/routeDetails/routeEditor/setSharedUsers")<
  User["id"][]
>();

export const buildRouteFromEditor = createAsyncAction(
  "route/routeDetails/routeEditor/buildRoute/request",
  "route/routeDetails/routeEditor/buildRoute/success",
  "route/routeDetails/routeEditor/buildRoute/failure"
)<void, RoutePreview, void>();

export const createRoute = createAsyncAction(
  "route/routeDetails/routeEditor/createRoute/request",
  "route/routeDetails/routeEditor/createRoute/success",
  "route/routeDetails/routeEditor/createRoute/failure"
)<{ skipRedirect?: boolean } | void, { route: Route }, void>();

export const saveRoute = createAsyncAction(
  "route/routeDetails/routeEditor/saveRoute/request",
  "route/routeDetails/routeEditor/saveRoute/success",
  "route/routeDetails/routeEditor/saveRoute/failure"
)<{ skipRedirect?: boolean } | void, { route: Route }, void>();

export const buildAndSaveRoute = createAction("route/routeDetails/routeEditor/buildAndSaveRoute")<{
  onFailure?: () => void;
  onSuccess?: () => void;
}>();

export type EditorActions = ActionType<
  | typeof addManualStop
  | typeof addPinsToRoute
  | typeof applyAllottedTimeToAllStops
  | typeof buildAndSaveRoute
  | typeof buildRouteFromEditor
  | typeof createRoute
  | typeof fetchScheduledActivities
  | typeof insertStop
  | typeof invertStops
  | typeof removeManualStop
  | typeof removePinsFromRoute
  | typeof removeScheduledActivity
  | typeof reorderStop
  | typeof saveRoute
  | typeof setSharedUsers
  | typeof updateDetails
  | typeof updateEndAddress
  | typeof updateEndEdgeType
  | typeof updateEndEntity
  | typeof updateManualStop
  | typeof updateRouteStops
  | typeof updateStartAddress
  | typeof updateStartEdgeType
  | typeof updateStartEntity
  | typeof updateStopDetails
>;
